import {
  archiveAssetApiAssetsAssetIdArchivePost,
  createAssetApiAssetsPost,
  createAssetFromExpenseApiAssetsFromExpenseExpenseIdPut,
  extractAndCreateAssetApiAssetsHomeHomeIdExtractPost,
  extractAndUpdateAssetApiAssetsAssetIdExtractPost,
  getAssetApiAssetsAssetIdGet,
  getAssetFromExpenseApiAssetsFromExpenseExpenseIdGet,
  linkAssetDocumentsApiAssetsAssetIdDocumentsPost,
  listAssetCategoriesApiAssetsCategoriesHomeHomeIdGet,
  listHomeAssetsPageApiAssetsHomeHomeIdPageGet,
  removeAssetImageApiAssetsAssetIdImageDelete,
  unlinkAssetDocumentApiAssetsAssetIdDocumentsDocumentIdDelete,
  updateAssetApiAssetsAssetIdPatch,
  uploadAssetImageApiAssetsAssetIdImagePost,
} from '@/client';
import type {
  AssetCreate,
  AssetDocumentLinkCreate,
  AssetFromExpenseCreate,
  AssetPageResponse,
  AssetResponse,
  AssetStatus,
  AssetUpdate,
  AssetCategoryResponse,
} from '@/client/types.gen';
import { createApiError, extractStatusCode } from '@/lib/api-error';
import { httpClient } from '@/lib/http-client';

export const assetKeys = {
  all: (homeId: string) => ['assets', homeId] as const,
  page: (homeId: string, filters: AssetListFilters = {}) =>
    ['assets', homeId, 'page', filters] as const,
  detail: (homeId: string, assetId: string) => ['assets', homeId, assetId] as const,
  fromExpense: (homeId: string, expenseId: string) =>
    ['assets', homeId, 'expense', expenseId] as const,
  categories: (homeId: string) => ['assetCategories', homeId] as const,
};

export interface AssetListFilters {
  limit?: number;
  offset?: number;
  status?: AssetStatus;
  categoryId?: string;
  search?: string;
}

function unwrap<T>(response: { data?: unknown; error?: unknown }): T {
  if (response.error) throw createApiError(response.error);
  return response.data as T;
}

export async function getAssetPage(
  homeId: string,
  { limit = 50, offset = 0, status, categoryId, search }: AssetListFilters = {},
): Promise<AssetPageResponse> {
  return unwrap(await listHomeAssetsPageApiAssetsHomeHomeIdPageGet({
    client: httpClient,
    path: { home_id: homeId },
    query: { limit, offset, status, category_id: categoryId, search: search || undefined },
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function getAssetCategories(homeId: string): Promise<AssetCategoryResponse[]> {
  return unwrap(await listAssetCategoriesApiAssetsCategoriesHomeHomeIdGet({
    client: httpClient,
    path: { home_id: homeId },
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function getAsset(assetId: string): Promise<AssetResponse> {
  return unwrap(await getAssetApiAssetsAssetIdGet({
    client: httpClient,
    path: { asset_id: assetId },
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function getAssetForExpense(expenseId: string): Promise<AssetResponse | null> {
  const response = await getAssetFromExpenseApiAssetsFromExpenseExpenseIdGet({
    client: httpClient,
    path: { expense_id: expenseId },
    headers: { authorization: '' },
    throwOnError: false,
  });
  if (response.error && extractStatusCode(response.error) === 404) return null;
  return unwrap<AssetResponse>(response);
}

export async function createAsset(data: AssetCreate): Promise<AssetResponse> {
  return unwrap(await createAssetApiAssetsPost({
    client: httpClient,
    body: data,
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function extractAndCreateAsset(
  homeId: string,
  url: string,
): Promise<AssetResponse> {
  return unwrap(await extractAndCreateAssetApiAssetsHomeHomeIdExtractPost({
    client: httpClient,
    path: { home_id: homeId },
    body: { url },
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function enrichAsset(assetId: string, url: string): Promise<AssetResponse> {
  return unwrap(await extractAndUpdateAssetApiAssetsAssetIdExtractPost({
    client: httpClient,
    path: { asset_id: assetId },
    body: { url },
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function createAssetFromExpense(
  expenseId: string,
  data: AssetFromExpenseCreate = {},
): Promise<AssetResponse> {
  return unwrap(await createAssetFromExpenseApiAssetsFromExpenseExpenseIdPut({
    client: httpClient,
    path: { expense_id: expenseId },
    body: data,
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function updateAsset(assetId: string, data: AssetUpdate): Promise<AssetResponse> {
  return unwrap(await updateAssetApiAssetsAssetIdPatch({
    client: httpClient,
    path: { asset_id: assetId },
    body: data,
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function uploadAssetImage(assetId: string, file: File): Promise<AssetResponse> {
  return unwrap(await uploadAssetImageApiAssetsAssetIdImagePost({
    client: httpClient,
    path: { asset_id: assetId },
    body: { file },
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function removeAssetImage(assetId: string): Promise<AssetResponse> {
  return unwrap(await removeAssetImageApiAssetsAssetIdImageDelete({
    client: httpClient,
    path: { asset_id: assetId },
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function archiveAsset(assetId: string): Promise<AssetResponse> {
  return unwrap(await archiveAssetApiAssetsAssetIdArchivePost({
    client: httpClient,
    path: { asset_id: assetId },
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function linkAssetDocuments(
  assetId: string,
  documents: AssetDocumentLinkCreate[],
): Promise<AssetResponse> {
  return unwrap(await linkAssetDocumentsApiAssetsAssetIdDocumentsPost({
    client: httpClient,
    path: { asset_id: assetId },
    body: { documents },
    headers: { authorization: '' },
    throwOnError: false,
  }));
}

export async function unlinkAssetDocument(
  assetId: string,
  documentId: string,
): Promise<AssetResponse> {
  return unwrap(await unlinkAssetDocumentApiAssetsAssetIdDocumentsDocumentIdDelete({
    client: httpClient,
    path: { asset_id: assetId, document_id: documentId },
    headers: { authorization: '' },
    throwOnError: false,
  }));
}
